/** "Add a source": name, where it sits in the tree, what kind it is, status, owner. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label, Modal, ModalFooter, Select, useToast } from "@/components/ui";
import { api } from "@/lib/api";
import { STATUS_LABEL } from "@/features/command/ui";
import { KINDS, STATUSES, errMsg, useTeam } from "./shared";
import type { HubSource, SourceRow } from "./types";

export function AddSourceModal({ open, onClose, sources, defaultParent }: {
  open: boolean;
  onClose: () => void;
  sources: HubSource[];
  defaultParent?: string | null;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const team = useTeam();
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const [kind, setKind] = useState("other");
  const [kindTouched, setKindTouched] = useState(false);
  const [status, setStatus] = useState("idea");
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setParent(defaultParent ?? ""); setKind("other"); setKindTouched(false); setStatus("idea"); setOwner(""); setSaving(false);
  }, [open, defaultParent]);

  // A sub-source usually works like its parent.
  useEffect(() => {
    if (kindTouched || !parent) return;
    const p = sources.find((s) => s.id === parent);
    if (p) setKind(p.kind);
  }, [parent, kindTouched, sources]);

  const tops = sources.filter((s) => !s.parent_id);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.post<{ source: SourceRow }>("/sources", {
        name: name.trim(),
        parent_id: parent || null,
        kind,
        status,
        ...(owner ? { owner_id: owner } : {}),
      });
      toast.success(`Added ${r.source.name}`, "Set up how we track it on its page.");
      onClose();
      navigate(`/traffic/${r.source.slug}`, { state: { from: "/traffic" } });
    } catch (e) {
      toast.error("Couldn't add the source", errMsg(e));
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add a source" description="A place customers come from, or one you plan to try.">
      <form
        onSubmit={(e) => { e.preventDefault(); void save(); }}
        className="space-y-4"
      >
        <div>
          <Label htmlFor="src-name" required>Name</Label>
          <Input id="src-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. YouTube collabs" maxLength={120} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="src-parent">Part of</Label>
            <Select id="src-parent" value={parent} onChange={(e) => setParent(e.target.value)}>
              <option value="">Nothing (its own source)</option>
              {tops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="src-kind">Kind</Label>
            <Select id="src-kind" value={kind} onChange={(e) => { setKind(e.target.value); setKindTouched(true); }}>
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="src-status">Status</Label>
            <Select id="src-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="src-owner">Owner</Label>
            <Select id="src-owner" value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Me</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
            </Select>
          </div>
        </div>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={saving} disabled={!name.trim()}>Add source</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
